import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { List } from 'immutable';
import { v4 } from 'uuid';
import { IConfirmDialogResult } from '../../../dialog/components/confirm-dialog/confirm-dialog.interface';
import { DialogService } from '../../../dialog/dialog.service';
import { PaymentTransaction } from '../../../document-data/sub-models/payment-transaction.model';
import {
  IMasterListItemAction,
  IMasterListItemData,
} from '../../../entity-master/components/master-list-item/master-list-item.interface';
import { TransactionActionsEnum } from '../../../payments-data/payments-data.data';
import { DropdownHorizontalAlignmentsEnum } from '../../../shared/components/dropdown/dropdown.component';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { ITransactionAction } from '../transaction-history/transaction-history.component';
import { TransactionHistoryListItemBuilder } from './transaction-history-list-item.builder';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';

@Component({
  selector: 'app-transaction-history-list-item',
  templateUrl: './transaction-history-list-item.component.html',
  styleUrls: ['./transaction-history-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [TransactionHistoryListItemBuilder],
})
export class TransactionHistoryListItemComponent implements OnInit, OnDestroy {
  @Input() paymentTransaction: PaymentTransaction;
  @Input() documentLocale: string;
  @Input() isAllAccess: boolean;
  @Input() documentTrackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() isDeposit: boolean;

  @Output()
  onAction: EventEmitter<ITransactionAction> = new EventEmitter<ITransactionAction>();

  private _deleteAction: IMasterListItemAction;
  private _refundAction: IMasterListItemAction;
  private _unmatchAction: IMasterListItemAction;

  refundConfirmDialogId: string;
  deleteConfirmDialogId: string;
  unmatchConfirmDialogId: string;
  paymentTransactionNotes: string;
  paymentTransactionData: List<IMasterListItemData>;
  paymentTransactionActions: List<List<IMasterListItemAction>>;
  DropdownHorizontalAlignmentsEnum: typeof DropdownHorizontalAlignmentsEnum;
  unmatchPrimaryButtonTrackingAction: ITrackingAction;
  unmatchSecondaryButtonTrackingAction: ITrackingAction;
  unmatchDialogTrackingObject: ITrackingObject;

  constructor(
    private _dialogService: DialogService,
    private _transactionHistoryListItemBuilder: TransactionHistoryListItemBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingObjectBuilder: TrackingObjectBuilder
  ) {}

  ngOnInit() {
    this.DropdownHorizontalAlignmentsEnum = DropdownHorizontalAlignmentsEnum;
    this.refundConfirmDialogId = v4();
    this.deleteConfirmDialogId = v4();
    this.unmatchConfirmDialogId = v4();

    this.paymentTransactionData = this._transactionHistoryListItemBuilder.buildListItemData(
      this.paymentTransaction,
      this.documentLocale,
      this.isDeposit
    );

    this.paymentTransactionNotes = this._transactionHistoryListItemBuilder.buildTransactionNotes(
      this.paymentTransaction
    );

    if (this.paymentTransaction.isRefundable()) {
      this._refundAction = this._transactionHistoryListItemBuilder.buildRefundAction(
        this.documentTrackingObject,
        this.trackingScreenName
      );
    }

    if (this.paymentTransaction.isUnmatchable()) {
      this._unmatchAction = this._transactionHistoryListItemBuilder.buildUnmatchAction(
        this.documentTrackingObject,
        this.trackingScreenName
      );
    }

    const onlyDeleteActionAvailable =
      !this.paymentTransaction.isRefundable() &&
      !this.paymentTransaction.isUnmatchable() &&
      this.paymentTransaction.isDeletable();

    if (onlyDeleteActionAvailable && this.isAllAccess) {
      this._deleteAction = this._transactionHistoryListItemBuilder.buildDeleteAction(
        this.documentTrackingObject,
        this.trackingScreenName
      );
    }

    this.paymentTransactionActions = this._transactionHistoryListItemBuilder.buildListItemActions(
      this.paymentTransaction,
      this._refundAction,
      this._unmatchAction,
      this._deleteAction,
      this.documentTrackingObject,
      this.trackingScreenName
    );

    // unmatch tracking
    this.unmatchPrimaryButtonTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'undo',
      this.trackingScreenName
    );
    this.unmatchSecondaryButtonTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'keep',
      this.trackingScreenName
    );
    this.unmatchDialogTrackingObject = this._trackingObjectBuilder.buildDialog(
      TrackingDialogNamesEnum.payments_undo_match
    );
  }

  onQuickAction(action: IMasterListItemAction) {
    switch (action.id) {
      case TransactionActionsEnum.delete_transaction:
        this.deleteTransaction();
        break;
      case TransactionActionsEnum.refund_transaction:
        this.refundTransaction();
        break;
      case TransactionActionsEnum.unmatch_transaction:
        this.unmatchTransaction();
        break;
      default:
        const transactionAction = this._transactionHistoryListItemBuilder.buildTransactionAction(
          action,
          this.paymentTransaction
        );

        this.onAction.emit(transactionAction);
        break;
    }
  }

  refundTransaction() {
    if (this.paymentTransaction.isRefundable()) {
      this._dialogService.openConfirmDialog(this.refundConfirmDialogId);
    }
  }

  deleteTransaction() {
    if (this.paymentTransaction.isDeletable()) {
      this._dialogService.openConfirmDialog(this.deleteConfirmDialogId);
    }
  }

  unmatchTransaction() {
    if (this.paymentTransaction.isUnmatchable()) {
      this._dialogService.openConfirmDialog(this.unmatchConfirmDialogId);
    }
  }

  onRefund({ result }: IConfirmDialogResult) {
    if (result) {
      const transactionAction = this._transactionHistoryListItemBuilder.buildTransactionAction(
        this._refundAction,
        this.paymentTransaction
      );

      this.onAction.emit(transactionAction);
    }
  }

  onDelete({ result }: IConfirmDialogResult) {
    if (result) {
      const transactionAction = this._transactionHistoryListItemBuilder.buildTransactionAction(
        this._deleteAction,
        this.paymentTransaction
      );

      this.onAction.emit(transactionAction);
    }
  }

  onUnmatch({ result }: IConfirmDialogResult) {
    if (result) {
      const transactionAction = this._transactionHistoryListItemBuilder.buildTransactionAction(
        this._unmatchAction,
        this.paymentTransaction
      );

      this.onAction.emit(transactionAction);
    }
  }

  ngOnDestroy() {
    this._dialogService.closeConfirmDialog(this.refundConfirmDialogId);
    this._dialogService.closeConfirmDialog(this.deleteConfirmDialogId);
    this._dialogService.closeConfirmDialog(this.unmatchConfirmDialogId);
  }
}
